<!-- Modern Processing Cycle Logs Dialog -->
<div class="modern-processing-dialog">
  <!-- Void Cycle Warning (if applicable) -->
  <div *ngIf="processingCycle?.Status === 'Void'" class="void-warning">
    <i class="pi pi-exclamation-triangle"></i>
    <span
      ><strong>Void Cycle:</strong> This processing cycle has been voided.</span
    >
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <!-- Skeleton for Tab Header -->
    <div class="skeleton-tab-header">
      <p-skeleton width="120px" height="42px" borderRadius="6px" />
    </div>

    <!-- Skeleton for Main Content -->
    <div class="skeleton-main-content">
      <!-- Left Panel - Processing Steps Skeleton -->
      <div class="skeleton-steps-panel">
        <!-- Panel Header -->
        <div class="skeleton-panel-header">
          <p-skeleton width="20px" height="20px" borderRadius="4px" />
          <p-skeleton width="140px" height="20px" borderRadius="4px" />
        </div>

        <!-- Steps List -->
        <div class="skeleton-steps-list">
          <div class="skeleton-step-item" *ngFor="let i of [1, 2, 3, 4]">
            <p-skeleton
              width="32px"
              height="32px"
              borderRadius="50%"
              styleClass="skeleton-step-circle"
            />
            <p-skeleton width="180px" height="16px" borderRadius="4px" />
          </div>
        </div>
      </div>

      <!-- Right Panel - Terminal Skeleton -->
      <div class="skeleton-terminal-panel">
        <!-- Terminal Header -->
        <div class="skeleton-terminal-header">
          <div class="skeleton-terminal-controls">
            <p-skeleton width="12px" height="12px" borderRadius="50%" />
            <p-skeleton width="12px" height="12px" borderRadius="50%" />
            <p-skeleton width="12px" height="12px" borderRadius="50%" />
          </div>
          <p-skeleton width="120px" height="16px" borderRadius="4px" />
        </div>

        <!-- Terminal Content -->
        <div class="skeleton-terminal-content">
          <div
            class="skeleton-log-line"
            *ngFor="let i of [1, 2, 3, 4, 5, 6, 7, 8]; let idx = index"
          >
            <p-skeleton width="140px" height="14px" borderRadius="3px" />
            <p-skeleton
              width="8px"
              height="14px"
              borderRadius="2px"
              styleClass="skeleton-separator"
            />
            <p-skeleton
              [width]="getSkeletonWidth(idx)"
              height="14px"
              borderRadius="3px"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content with Modern Layout -->
  <div *ngIf="!isLoading && processingCycleLogsByAttempt" class="main-content">
    <!-- Attempts Tabs -->
    <div class="attempts-section">
      <p-tabs
        [(value)]="activeTabIndex"
        (onChange)="onTabChange(activeTabIndex)"
        styleClass="attempts-tabs"
      >
        <p-tablist>
          <p-tab
            *ngFor="
              let attempt of getReversedAttempts();
              let i = index;
              trackBy: trackByAttempt
            "
            [value]="i"
          >
            <div class="tab-header">
              <span class="tab-title">Attempt #{{ attempt }}</span>
              <p-badge
                [value]="i === 0 ? 'Latest' : ''"
                [severity]="getAttemptBadgeSeverity(attempt)"
                class="attempt-badge"
              />
            </div>
          </p-tab>
        </p-tablist>

        <p-tabpanels>
          <p-tabpanel
            *ngFor="
              let attempt of getReversedAttempts();
              let i = index;
              trackBy: trackByAttempt
            "
            [value]="i"
          >
            <!-- Tab Content with Fixed Grid Layout -->
            <div class="tab-content">
              <div class="panels-container">
                <!-- Left Panel - Processing Steps -->
                <div class="steps-panel">
                  <div class="panel-header">
                    <i class="pi pi-list-check"></i>
                    <h3>Processing Steps</h3>
                  </div>

                  <div class="steps-container">
                    <div class="steps-list">
                      <div
                        *ngFor="
                          let stage of processingCycleStagesByAttempt[attempt];
                          let i = index;
                          trackBy: trackByStageIndex
                        "
                        class="step-item"
                        [ngClass]="{
                          'step-success': stage.Status === 'Success',
                          'step-failed': stage.Status === 'Failed',
                          'step-queued': stage.Status === 'Queued',
                        }"
                      >
                        <div class="step-indicator">
                          <div class="step-icon"></div>
                          <div
                            *ngIf="
                              i <
                              processingCycleStagesByAttempt[attempt].length - 1
                            "
                            class="step-line"
                          ></div>
                        </div>
                        <div class="step-content">
                          <div class="step-label">
                            {{ formatStageName(stage.Stage) }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Fallback if no steps -->
                    <div
                      *ngIf="
                        !processingCycleStagesByAttempt[attempt] ||
                        processingCycleStagesByAttempt[attempt].length === 0
                      "
                      class="no-steps"
                    >
                      <i class="pi pi-info-circle"></i>
                      <span>No step information available</span>
                    </div>
                  </div>
                </div>

                <!-- Right Panel - Terminal-style Logs -->
                <div class="terminal-panel">
                  <div class="panel-header">
                    <div class="terminal-header">
                      <div class="terminal-controls">
                        <span class="control-dot red"></span>
                        <span class="control-dot yellow"></span>
                        <span class="control-dot green"></span>
                      </div>
                      <span class="terminal-title">Processing Logs</span>
                    </div>
                  </div>

                  <div class="terminal-content">
                    <div
                      *ngIf="
                        processingCycleLogsByAttempt[attempt] &&
                          processingCycleLogsByAttempt[attempt].length > 0;
                        else noLogs
                      "
                      class="log-lines"
                    >
                      <div
                        *ngFor="
                          let log of processingCycleLogsByAttempt[attempt];
                          trackBy: trackByLogIndex
                        "
                        class="log-line"
                      >
                        <span class="log-timestamp">{{
                          formatDate(log.CreatedUtc)
                        }}</span>
                        <span class="log-separator">│</span>
                        <span class="log-message">{{ log.Description }}</span>
                      </div>
                    </div>

                    <ng-template #noLogs>
                      <div class="no-logs">
                        <i class="pi pi-file"></i>
                        <span
                          >No processing logs available for this attempt</span
                        >
                      </div>
                    </ng-template>
                  </div>
                </div>
              </div>
            </div>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    </div>
  </div>

  <!-- Help Footer -->
  <div class="help-footer">
    <i class="pi pi-info-circle"></i>
    <span>
      <strong>Processing Logs:</strong> Detailed execution logs for each
      processing attempt.
    </span>
  </div>
</div>
